import { FullAuditedEntityDto, PagedAndSortedResultRequestDto } from '@abp/ng.core';
import {
  ChannelIntegrationDto,
  ChannelIntegrationCreateUpdateDto,
  ServiceIntegrationCreateUpdateDto,
  ServiceIntegrationDto,
} from 'src/app/integrations/proxy/integration.model';
import { AgentCreateDto, VirtualAgent } from 'src/app/virtual-agents/proxy';
import { FlowType, ProjectVersionDto } from '../../shared/designer/proxy/designer.model';
import { OutboundRulesCreateUpdateDto } from './outbound/outbound-rules.models';
import { AiInsightCreateUpdateDto } from './ai-insights/ai-insights.models';

export interface GetProjectsInput extends PagedAndSortedResultRequestDto {
  filterText?: string;
  name?: string;
  description?: string;
}

export type GetCustomDataKeysInput = PagedAndSortedResultRequestDto;

export interface ProjectCreateUpdateDto {
  name: string;
  description: string;
  primaryLanguageCode: string;
  areAiInsightsEnabled?: boolean;
  isOutbound: boolean;
  additionalLanguageCodes: string[];
  channelIntegrations: ChannelIntegrationCreateUpdateDto[];
  serviceIntegrations: ServiceIntegrationCreateUpdateDto[];
  virtualAgents: AgentCreateDto[];
  outboundRules: OutboundRulesCreateUpdateDto;
  aiInsights: AiInsightCreateUpdateDto[];
}

export interface ProjectPublishDto {
  channelIntegrations: ChannelIntegrationCreateUpdateDto[];
  serviceIntegrations: ServiceIntegrationCreateUpdateDto[];
  isGracefulPublishEnabled: boolean;
}

export interface ProjectVirtualAgentDto {
  name: string;
  id: string;
}

export interface ProjectDto extends FullAuditedEntityDto<string> {
  name?: string;
  currentVersionId?: string;
  publishedProjectVersionId?: string;
  publishedDate?: Date | string;
  isGracefulPublishEnabled?: boolean;
  isKnovvuAnalyticsEnabled?: boolean;
  isSpellCorrectionEnabled?: boolean;
  isTranslationServiceEnabled?: boolean;
  projectVersions?: ProjectVersionDto[];
  channelIntegrations?: ChannelIntegrationDto[];
  serviceIntegrations?: ServiceIntegrationDto[];
  virtualAgents?: ProjectVirtualAgentDto[];
  isProjectFlowValid: boolean;
}

export interface ProjectFilterDto {
  id: string;
  name?: string;
  virtualAgents?: ProjectVirtualAgentDto[];
}

export interface IntentFilterDto {
  virtualAgentName: string;
  intentName: string;
}

export interface ProjectWithDetailsDto extends FullAuditedEntityDto<string> {
  name?: string;
  description?: string;
  timezone?: string;
  areAiInsightsEnabled?: boolean;
  isGracefulPublishEnabled?: boolean;
  isKnovvuAnalyticsEnabled?: boolean;
  isSpellCorrectionEnabled?: boolean;
  isTranslationServiceEnabled?: boolean;
  isSsmlSupportEnabled?: boolean;
  isOutbound?: boolean;
  channelIntegrations?: ChannelIntegrationDto[];
  serviceIntegrations?: ServiceIntegrationDto[];
  confirmationPhrases?: ConfirmationPhraseDto[];
  virtualAgents?: VirtualAgent[];
  projectVersion?: ProjectVersionDto;
}

export interface PhraseTag {
  id: string;
  value: string;
}

export interface LanguageConfirmationPhrasesModel {
  positivePhrases: PhraseTag[];
  negativePhrases: PhraseTag[];
}

export interface ConfirmationPhraseDto extends FullAuditedEntityDto<string> {
  value: string;
  languageId: string;
  projectId: string;
  type: ConfirmationPhraseType;
}

export interface ConfirmationPhraseUpdateDto {
  id?: string;
  value: string;
  languageId: string;
  projectId: string;
  type: ConfirmationPhraseType;
}

export enum ConfirmationPhraseType {
  positive = 0,
  negative = 1,
}

export enum TrainStatus {
  notTrained = 0,
  trained = 1,
}

export enum GenerativeQuestionAnsweringTrainStatus {
  notTrained = 0,
  training = 1,
  trained = 2,
  obsolete = 3,
  failed = 4,
  deprecated = 5,
}

export enum GenerativeQuestionAnsweringFileType {
  general = 0,
  faq = 1,
}

export enum IntegrationUsageStatus {
  unused,
  draft,
  published,
  both,
}

export interface ChatParametersModel {
  tenant: string;
  project: string;
  isPublished: boolean;
  isTest: boolean;
  flowId: string;
  hasVoiceCallCapability: boolean;
}

export interface ProjectImportDto {
  file: File;
}

export interface TimeZoneInfoDtoPagedResultDto {
  items?: TimeZoneInfoDto[];
  totalCount: number;
}

export interface TimeZoneInfoDto {
  displayName?: string;
}

export interface KnovvuAnalyticsSettingsDto {
  isKnovvuAnalyticsToggleDisabled: boolean;
}

export interface ProjectFlowsAndVirtualAgentsDto {
  projectId: string;
  projectName: string;
  projectVersionId: string;
  draftProjectVersionId: string;
  publishedProjectVersionId: string;
  isPublishedVersion: boolean;
  hasPublishedVersion: boolean;
  isOutboundProject: boolean;
  areAiInsightsEnabled: boolean;
  generativeQuestionAnsweringTrainStatus: GenerativeQuestionAnsweringTrainStatus;
  version: number;
  lastModificationTime: Date;
  publishedDate: Date;
  hasSRIntegration: boolean;
  hasTTSIntegration: boolean;
  flowSummaries: ProjectFlowSummaryDto[];
  virtualAgentSummaries: ProjectVirtualAgentSummaryDto[];
}

export interface FlowVaNavigatorMenuItemModel {
  id: string;
  name: string;
  reducedName: string;
}

export interface ProjectFlowSummaryDto extends FlowVaNavigatorMenuItemModel {
  flowType: FlowType;
}

export type ProjectVirtualAgentSummaryDto = FlowVaNavigatorMenuItemModel;

export interface GenerativeQuestionAnsweringPromptDto {
  prompt: string;
}

export interface GenerativeQuestionAnsweringFileDto {
  originalName: string;
  contentType: string;
  storageObjectName: string;
  sizeInBytes: number;
  fileType: GenerativeQuestionAnsweringFileType;
}

export interface GenerativeQuestionAnsweringUrlDto {
  url: string;
  excludeUrls: GenerativeQuestionAnsweringExcludeUrlDto[];
}

export interface GenerativeQuestionAnsweringUrlCreateDto {
  url: string;
  excludeUrls: GenerativeQuestionAnsweringExcludeUrlCreateDto[];
}

export interface GenerativeQuestionAnsweringExcludeUrlDto extends FullAuditedEntityDto<string> {
  url: string;
}

export interface GenerativeQuestionAnsweringExcludeUrlCreateDto {
  url: string;
}

export interface GenerativeQuestionAnsweringDataDto extends FullAuditedEntityDto<string> {
  file: GenerativeQuestionAnsweringFileDto;
  url: GenerativeQuestionAnsweringUrlDto;
  trainStatus: TrainStatus;
  isRemoved: boolean;
  isFile: boolean;
}

export interface GenerativeQuestionAnsweringTrainStatusDto {
  status: GenerativeQuestionAnsweringTrainStatus;
  trainDisabled: boolean;
  trainTooltip: string;
  trainLoading: boolean;
  trainLoadingTooltip: string;
}

export interface GenerativeQuestionAnsweringTrainedNotificationModel {
  projectId: string;
  name: string;
}

export interface GenerativeQuestionAnsweringTrainFailedNotificationModel {
  projectId: string;
  name: string;
}

export interface PublishExportStatusDto {
  currentVersionId: string;
  isPublishButtonEnabled: boolean;
  isExportButtonEnabled;
  publishTooltip: string;
  exportTooltip: string;
}

export interface ProjectPublishStatusDto {
  canBePublished: boolean;
  canBeExported: boolean;
  publishErrorMessage: string;
  exportErrorMessage: string;
}

export interface GenerateProjectDto {
  name: string;
  description: string;
  intentCount: number;
  utteranceCount: number;
  primaryLanguageCode: string;
}
